import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Spin,
  Table,
  Popover,
  Button,
  Space,
} from "antd";
import {
  CheckCircleOutlined,
  ProjectOutlined,
  FileOutlined,
  UserOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CheckOutlined,
  DownOutlined,
} from "@ant-design/icons";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";
import * as XLSX from "xlsx";

const { Title } = Typography;

// Helper function to format date with suffix
const formatDateWithSuffix = (date) => {
  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";

  const month = date.toLocaleString("default", { month: "long" });
  const year = date.getFullYear();

  return `${day}${suffix} ${month} ${year}`;
};

const Dashboard = ({ isDarkMode }) => {
  const { jsonApi } = useApis();

  const [totalUsers, setTotalUsers] = useState(null);
  const [totalSurveys, setTotalSurveys] = useState(null);
  const [completedSurveys, setCompletedSurveys] = useState(null);
  const [approvedSurveys, setApprovedSurveys] = useState(null);
  const [rejectedSurveys, setRejectedSurveys] = useState(null);
  const [inProgressSurveys, setInProgressSurveys] = useState(null);
  const [pendingSurveys, setPendingSurveys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [userData, setUserData] = useState([]);
  const [userLoading, setUserLoading] = useState(true);

  const [totalSurveysToday, setTotalSurveysToday] = useState(0);
  const [totalSurveysCount, setTotalSurveysCount] = useState(0);
  const [totalSurveysYesterday, setTotalSurveysYesterday] = useState(0);

  const cardStyle = {
    background: isDarkMode ? "#1f1f1f" : "#ffffff",
    borderColor: isDarkMode ? "#303030" : "#f0f0f0",
  };

  const textStyle = {
    color: isDarkMode ? "#ffffff" : "rgba(0, 0, 0, 0.85)",
  };

  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const formattedToday = formatDateWithSuffix(today);
  const formattedYesterday = formatDateWithSuffix(yesterday);

  const exportToExcel = () => {
    const dataToExport = userData.map((user, index) => ({
      "S.No": index + 1,
      "User Name": user.name,
      
      "Approved Surveys": user.approved_surveys || 0,
      "Rejected Surveys": user.rejected_surveys || 0,
      "In Progress Surveys": user.in_progress_surveys || 0,
      "Total Surveys": user.total_surveys || 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "User Survey Data");

    XLSX.writeFile(workbook, "user_survey_data.xlsx");
  };

  useEffect(() => {
    const fetchTotalData = async () => {
      try {
        const data = await jsonApi(appConstant.TotalData, {}, null, null);
        const stats = data?.data?.data;
        setTotalUsers(stats?.totalUsers);
        setTotalSurveys(stats?.totalSurveys);
        setCompletedSurveys(stats?.completedSurveys);
        setPendingSurveys(stats?.pendingSurveys);
        setApprovedSurveys(stats?.approvedSurveys);
        setRejectedSurveys(stats?.rejectedSurveys);
        setInProgressSurveys(stats?.inProgressSurveys);
      } catch (error) {
        console.error("Error fetching total data:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchUserData = async () => {
      try {
        const response = await jsonApi(appConstant.DashboardUserData, {}, null, null);
        const data = response?.data?.data || {};
        const users = Array.isArray(data.users) ? data.users.map(user => ({
          ...user,
          approved_surveys: user.approved_surveys || 0,
          rejected_surveys: user.rejected_surveys || 0,
          in_progress_surveys: user.in_progress_surveys || 0,
        })) : [];

        setUserData(users);
        setTotalSurveysToday(data.total_surveys_today || 0);
        setTotalSurveysYesterday(data.total_surveys_yesterday || 0);
        setTotalSurveysCount(data.completedSurveys || 0);
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserData([]);
      } finally {
        setUserLoading(false);
      }
    };

    fetchTotalData();
    fetchUserData();
  }, []);

  const renderValue = (value) => (loading ? <Spin size="small" /> : value ?? 0);

  const columns = [
    {
      title: "S.No",
      key: "serialNumber",
      render: (_, __, index) => {
        const { current, pageSize } = pagination;
        return (current - 1) * pageSize + index + 1;
      },
      width: "5%",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={textStyle}>{text}</span>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (text) => <span style={textStyle}>{text}</span>,
    },
    {
      title: (
        <>
          Today Total ({totalSurveysToday})
          <br />
          ({formattedToday})
        </>
      ),
      dataIndex: "surveys_today",
      key: "surveys_today",
    },
    {
      title: (
        <>
          Yesterday Total ({totalSurveysYesterday})
          <br />
          ({formattedYesterday})
        </>
      ),
      dataIndex: "surveys_yesterday",
      key: "surveys_yesterday",
    },
    {
      title: (
        <>
          Total Survey
          <br />
          ({totalSurveysCount})
        </>
      ),
      dataIndex: "total_surveys",
      key: "total_surveys",
    },
    {
      title: "Survey Details",
      key: "survey_breakdown",
      render: (_, record) => {
        const content = (
          <div>
            <p>✅ Approved Surveys: {record.approved_surveys}</p>
            <p>❌ Rejected Surveys: {record.rejected_surveys}</p>
            <p>⏳ In-Progress Surveys: {record.in_progress_surveys}</p>
          </div>
        );

        return (
          <Popover content={content} title="Survey Details" trigger="click">
            <Button size="small" style={{ backgroundColor: 'white', borderColor: '#1890ff' }}>
              View <DownOutlined />
            </Button>
          </Popover>
        );
      },
    },
  ];

  return (
    <div>
      <Title level={2} style={{ ...textStyle, marginBottom: 24 }}>
        Dashboard
      </Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card style={cardStyle}>
            <Statistic
              title={<span style={textStyle}>Total Users</span>}
              value={renderValue(totalUsers)}
              prefix={<UserOutlined style={{ color: "#1890ff" }} />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card style={cardStyle}>
            <Statistic
              title={<span style={textStyle}>Total Surveys</span>}
              value={renderValue(totalSurveys)}
              prefix={<FileOutlined style={{ color: "#52c41a" }} />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} lg={12}>
          <Card style={cardStyle}>
            <Row gutter={[24, 16]} justify="start" align="middle">
              <Col span={6}>
                <Statistic
                  title={<span style={textStyle}>Completed Surveys</span>}
                  value={renderValue(completedSurveys)}
                  prefix={<CheckOutlined style={{ color: "#13c2c2" }} />}
                  valueStyle={{ color: "#13c2c2" }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={<span style={textStyle}>Approved Surveys</span>}
                  value={renderValue(approvedSurveys)}
                  prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={<span style={textStyle}>Rejected Surveys</span>}
                  value={renderValue(rejectedSurveys)}
                  prefix={<CloseCircleOutlined style={{ color: "#ff4d4f" }} />}
                  valueStyle={{ color: "#ff4d4f" }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={<span style={textStyle}>In-progress Surveys</span>}
                  value={renderValue(inProgressSurveys)}
                  prefix={<SyncOutlined style={{ color: "#faad14" }} spin />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card style={cardStyle}>
            <Statistic
              title={<span style={textStyle}>Pending Surveys</span>}
              value={renderValue(pendingSurveys)}
              prefix={<ProjectOutlined style={{ color: "#722ed1" }} />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        style={{ ...cardStyle, marginTop: 24 }}
        title={(
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <span style={textStyle}>User Survey Data</span>
            <Button type="primary" onClick={exportToExcel} style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>Export Excel</Button>
          </Space>
        )}
      >
        <Table
          dataSource={userData}
          columns={columns}
          loading={userLoading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50"],
            onChange: (page, pageSize) => {
              setPagination({ current: page, pageSize: pageSize });
            },
          }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;
